import { useCallback, useLayoutEffect, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import type { Delta, EmitterSource } from 'quill/core';
import Quill from 'quill/core';
import { useQuill } from '../editor/QuillContext';
import type { DocumentType } from '../graphql/generated';
import { gql } from '../graphql/generated';
import { getMagneticWallItemElementsWithIds } from './MagneticWall';
import { TYPING_DEBOUNCE_TIMEOUT } from './constants';

const POST_FRAGMENT = gql(`
    fragment useHasMagneticWallItems_post on PublicPostFields {
        id
        threads {
            id
        }
        annotations {
            id
        }
    }
`);

const useHasMagneticWallItems = (
  post: DocumentType<typeof POST_FRAGMENT> | undefined,
  skip?: boolean,
) => {
  const { quill } = useQuill();

  const [hasMagneticWallItems, setHasMagneticWallItems] = useState(false);

  const checkAndSetMagneticWallItemState = useCallback(() => {
    const hasThreadsOrAnnotations =
      (!!post?.threads && post.threads.length > 0) ||
      (!!post?.annotations && post.annotations.length > 0);

    const hasElements =
      quill && hasThreadsOrAnnotations
        ? getMagneticWallItemElementsWithIds(quill.container).length > 0
        : false;

    setHasMagneticWallItems(hasElements);
  }, [quill, post?.threads, post?.annotations]);

  const debounceCheckAndSetMagneticWallItemState = useDebouncedCallback(
    checkAndSetMagneticWallItemState,
    TYPING_DEBOUNCE_TIMEOUT,
  );

  useLayoutEffect(() => {
    if (!quill) return undefined;

    if (skip) {
      setHasMagneticWallItems(false);
      return undefined;
    }

    checkAndSetMagneticWallItemState();

    const onTextChange = (
      _delta: Delta,
      _old: Delta,
      source: EmitterSource,
    ) => {
      if (source === 'user') {
        debounceCheckAndSetMagneticWallItemState.callback();
      }
    };

    quill.on(Quill.events.TEXT_CHANGE, onTextChange);

    return () => {
      quill.off(Quill.events.TEXT_CHANGE, onTextChange);
      debounceCheckAndSetMagneticWallItemState.cancel();
    };
  }, [
    quill,
    checkAndSetMagneticWallItemState,
    debounceCheckAndSetMagneticWallItemState,
    skip,
  ]);

  return hasMagneticWallItems;
};

export default useHasMagneticWallItems;
